import { useEffect, useMemo, useState } from "react";
import { Badge, Banner, BlockStack, Box, Button, ButtonGroup, Card, DataTable, InlineStack, Link, Text, TextField, Tooltip } from "@shopify/polaris";
import { getRequest, truncate } from "../../../shared/components/functions";
import { PageSpinner } from "../../../shared/components/spinner/PageSpinner";
import { CardHeading } from "../../../shared/components/CardHeading";

const STATUS_META = {
  critical: { label: "Critical", tone: "critical", help: "Needed quantity is still short after current stock and open purchase orders are counted." },
  incoming: { label: "Incoming covers", tone: "attention", help: "Current stock is short, but open purchase orders cover the calculated shortage." },
  move: { label: "Move stock", tone: "info", help: "Warehouse stock alone is short, but warehouse plus Trianval stock covers the demand." },
  ok: { label: "OK", tone: "success", help: "Warehouse stock covers the calculated material demand." },
  needs_order: { label: "Needs order", tone: "critical", help: "The product has open demand but no resolved BOM or tree components, so the shortage stays on the parent product." },
  needs_production: { label: "Needs production", tone: "attention", help: "Finished-product demand is not covered by warehouse stock or open work orders, and components were resolved." },
  planned: { label: "Planned", tone: "info", help: "Open work orders cover the calculated finished-product shortage." },
  ready: { label: "Ready", tone: "success", help: "Warehouse stock covers the open finished-product demand." },
};

const ORDER_STATUS_META = {
  producible: {
    label: "Producible",
    tone: "success",
    help: "All open lines on this order can be covered by current finished stock or by reserving current raw-material stock after earlier orders have reserved first.",
  },
  partial: {
    label: "Partial",
    tone: "attention",
    help: "Some stock can be reserved for this order, but at least one required part is short after earlier orders have taken priority.",
  },
  blocked: {
    label: "Blocked",
    tone: "critical",
    help: "The order has missing parts or unresolved BOM/tree data and cannot be produced from current stock in this simulated queue.",
  },
  covered: {
    label: "Stock",
    tone: "success",
    help: "This line is covered by finished-product warehouse stock, so no production is needed for it in this simulation.",
  },
  buildable: {
    label: "Buildable",
    tone: "info",
    help: "This line needs production, and all required leaf parts could be reserved from current material stock.",
  },
};

const SOURCE_META = {
  TREE: {
    label: "Tree",
    tone: "info",
    help: "Greg/Jack machine demand. The PMG/PMJ configuration string selects the matching tree table and variant column; enabled descendants under the root are multiplied through the nested tree quantities.",
  },
  BOM: {
    label: "BOM",
    tone: undefined,
    help: "Standard AHA bill-of-material demand. BOM entries are recursively expanded until the leaf components are reached.",
  },
  NO_BOM: {
    label: "No BOM",
    tone: "critical",
    help: "No BOM components were found for this product, so the shortage is kept on the product itself.",
  },
  TREE_WITHOUT_PARTS: {
    label: "Tree missing",
    tone: "critical",
    help: "The machine tree or selected variant could not be resolved to leaf parts, so the shortage is kept on the machine itself.",
  },
};

const SUMMARY_HELP = {
  openOrderLines: "Open fulfilment sales lines for the Perfect Moose supplier scope where ordered quantity minus fulfilled quantity is greater than zero.",
  machineProductionSlots: "Unfulfilled Greg/Jack machine lines converted into individual machine slots after already connected serials are distributed over the oldest open lines.",
  finalProductsWithProductionGap: "Finished products whose shortage is not fully covered by open work orders.",
  materials: "Unique raw/leaf materials created by exploding product BOMs and machine trees for the uncovered demand.",
  materialsWithCurrentShortage: "Materials where needed quantity is greater than warehouse plus Trianval stock before purchase orders are considered.",
  criticalMaterials: "Materials where a shortage remains even after open purchase orders are deducted.",
};

const MATERIAL_METRIC_HELP = {
  needed: "Sum of material demand from shortage-driving order lines. For components this is final shortage quantity times quantity per assembly.",
  monthlyConsumption:
    "Weighted average monthly usage from the last six completed calendar months. Negative stock changes are converted to positive consumption, positive stock changes are ignored, and newer months get more weight.",
  available: "Current stock in warehouse stock place 1 plus Trianval stock place 9. Negative stock quantities are treated as 0.",
  shortNow: "max(Needed - Available, 0). Incoming purchase orders are not included here.",
  shortAfterPo: "max(Short now - open incoming purchase quantity, 0).",
};

const PRODUCTION_METRIC_HELP = {
  openDemand: "Open finished-product demand after Schuilenburg-to-AHA translation ratios and machine slotting are applied.",
  warehouseStock: "AHA warehouse stock in stock place 1. Negative stock is treated as 0. Machine tree demand is treated as build-to-order, so finished-machine stock is not used to cover tree slots.",
  needsProduction: "Open demand that remains after warehouse stock is allocated to the earliest due orders.",
  newWorkOrderGap: "Needs production minus open work orders for the finished product.",
};

const ORDER_METRIC_HELP = {
  priority: "Virtual reservation position. Orders are sorted by parsed delivery date first, then order id.",
  finishedReserved: "Finished-product warehouse stock reserved for this order after earlier orders have reserved first. Machine tree demand is treated as build-to-order.",
  productionQty: "Open order quantity that still needs production after finished-product stock is reserved.",
  materialReserved: "Raw/leaf material quantity reserved from current warehouse plus Trianval stock for this order.",
  missingMaterial: "Raw/leaf material quantity still missing after earlier orders and this order have reserved current stock.",
};

const MATERIAL_COLUMNS = [
  { label: "Material", help: "Raw or leaf component that is needed for currently uncovered fulfilment demand." },
  { label: "Needed", help: MATERIAL_METRIC_HELP.needed },
  { label: "Avg/mo", help: MATERIAL_METRIC_HELP.monthlyConsumption },
  { label: "Stock", help: "WH is warehouse stock place 1, TR is Trianval stock place 9, PO is open purchase quantity not yet received." },
  { label: "Short after PO", help: MATERIAL_METRIC_HELP.shortAfterPo },
  { label: "Earliest due", help: "Earliest due date among the order lines that create demand for this material." },
  { label: "Status", help: "Calculated from needed quantity, warehouse stock, Trianval stock, and incoming purchase orders." },
  { label: "Inspect", help: "Shows the demand breakdown for this material." },
];

const USED_BY_COLUMNS = [
  { label: "Product", help: "Finished product, machine variant, or parent product that uses this material." },
  { label: "Qty", help: "Total material quantity contributed by this parent product or variant." },
  { label: "Needed by", help: "Earliest due date among the parent product's shortage-driving orders." },
  { label: "Orders", help: "Sales orders behind this material demand." },
];

const MATERIAL_ORDER_COLUMNS = [
  { label: "Order", help: "Sales order that contributes to this material need." },
  { label: "Customer", help: "Customer on the sales order." },
  { label: "Due", help: "Parsed delivery term used for planning order pressure." },
  { label: "For", help: "Finished product or machine variant that creates the material demand." },
  { label: "Qty", help: "Material quantity needed for this order line after BOM/tree multiplication." },
];

const CONSUMPTION_COLUMNS = [
  { label: "Month", help: "Completed calendar month used for the consumption average." },
  { label: "Used", help: "Absolute value of negative stock changes for this part in that month. Positive stock changes are ignored." },
  { label: "Weight", help: "Oldest month is weight 1 and newest month is weight 6." },
];

const PRODUCTION_COLUMNS = [
  { label: "Product", help: "Finished product demand. Tree rows are Greg/Jack machine variants; BOM rows are normal AHA BOM products." },
  { label: "Demand", help: PRODUCTION_METRIC_HELP.openDemand },
  { label: "WH", help: PRODUCTION_METRIC_HELP.warehouseStock },
  { label: "To make", help: PRODUCTION_METRIC_HELP.needsProduction },
  { label: "Open WO", help: "Open work-order quantity for this finished product." },
  { label: "Gap", help: PRODUCTION_METRIC_HELP.newWorkOrderGap },
  { label: "Due", help: "Earliest due date among the shortage-driving order lines." },
  { label: "Status", help: "Calculated from finished-product shortage, open work orders, and whether components were resolved." },
  { label: "Inspect", help: "Shows component and order breakdown for this product." },
];

const COMPONENT_COLUMNS = [
  { label: "Part", help: "Component resolved from the product BOM or machine tree. Indentation follows tree/BOM depth." },
  { label: "Kind", help: "Raw means a leaf component. Assembly means the row has children and is shown for context, not raw material pressure." },
  { label: "Qty/unit", help: "Quantity per finished product after multiplying quantities through every parent level." },
  { label: "Qty needed", help: "Qty/unit times the finished-product shortage quantity." },
  { label: "Stock", help: "WH warehouse stock, TR Trianval stock, and PO open purchase quantity for this component." },
  { label: "Gap", help: "max(Qty needed - WH - TR, 0). Purchase orders are shown in Stock but this component gap is the current physical-stock gap." },
];

const SHORTAGE_ORDER_COLUMNS = [
  { label: "Order", help: "Sales order that is not covered by finished-product warehouse stock." },
  { label: "Customer", help: "Customer on the sales order." },
  { label: "Reference", help: "Customer reference from the sales order." },
  { label: "Due", help: "Parsed delivery term used for shortage allocation." },
  { label: "Qty", help: "Finished-product quantity that still needs production or ordering for this order." },
];

const ORDER_COLUMNS = [
  { label: "Order", help: "Open fulfilment order in the virtual priority queue. Earlier rows reserve stock before later rows." },
  { label: "Customer", help: "Customer and reference from the sales order." },
  { label: "Due", help: "Parsed delivery term used to order the simulated reservation queue." },
  { label: "Lines", help: "Open fulfilment lines on this order after machine slots and translated quantities are considered." },
  { label: "To build", help: ORDER_METRIC_HELP.productionQty },
  { label: "Missing", help: ORDER_METRIC_HELP.missingMaterial },
  { label: "Problems", help: "Distinct parts or unresolved product structures blocking this order." },
  { label: "Status", help: "Whether the order can be produced from current stock after previous orders have reserved first." },
  { label: "Inspect", help: "Shows the virtual reservation and bottlenecks for this order." },
];

const ORDER_LINE_COLUMNS = [
  { label: "Product", help: "Open order line or individual machine slot being checked for producibility." },
  { label: "Open", help: "Open quantity for this line in the order simulation." },
  { label: "Finished", help: "Finished-product warehouse stock reserved for this line. Tree-machine rows do not consume finished stock." },
  { label: "To build", help: "Quantity that must be produced after finished stock is reserved." },
  { label: "Missing", help: "Sum of missing raw/leaf parts for this line." },
  { label: "Status", help: "Covered means finished stock covers it; buildable means parts are available; blocked means at least one part is short or unresolved." },
];

const ORDER_BOTTLENECK_COLUMNS = [
  { label: "Part", help: "Raw/leaf part or unresolved parent product that prevents the order from being fully producible." },
  { label: "Needed", help: "Quantity needed by this order after BOM/tree multiplication." },
  { label: "Reserved", help: "Quantity this order could still reserve from current stock after previous orders." },
  { label: "Missing", help: "Needed minus reserved for this order." },
  { label: "Stock before", help: "Virtual current stock left for this part immediately before this order tried to reserve it." },
  { label: "Used by", help: "Product or machine line on this order that needs the part." },
  { label: "Reason", help: "Why the part blocks the order." },
];

const PLAN_ASSUMPTIONS = [
  "Normal products use the AHA BOM recursively; Greg/Jack machine products use the configured product tree variant from the PMG/PMJ code in the sales line text.",
  "Tree quantities follow the nested-set tree structure: each enabled child quantity is multiplied by its enabled parent quantity down to the leaf part.",
  "Only leaf components become raw-material pressure. Assemblies are shown in the production detail for context.",
  "Warehouse stock is allocated to the earliest due finished-product demand first. Open work orders reduce the production gap; open purchase orders reduce material shortages after current stock.",
  "Monthly consumption uses the last six completed calendar months. It averages negative stock changes with weights 1 to 6, oldest to newest.",
  "Negative quantities from stock inaccuracies are treated as 0 before planning calculations are made.",
  "If a BOM, tree, or machine configuration cannot be resolved, the demand remains visible as a direct shortage instead of being hidden.",
];

const tooltipContent = (content) => <div style={{ maxWidth: "320px", whiteSpace: "normal" }}>{content}</div>;

const HelpLabel = ({ label, content }) => {
  if (!content) return label;

  return (
    <Tooltip content={tooltipContent(content)} width="wide">
      <span style={{ borderBottom: "1px dotted #8c9196", cursor: "help" }}>{label}</span>
    </Tooltip>
  );
};

const ExplainedBadge = ({ children, content }) => {
  if (!content) return children;

  return (
    <Tooltip content={tooltipContent(content)} width="wide">
      <span style={{ display: "inline-flex" }}>{children}</span>
    </Tooltip>
  );
};

const dataHeadings = (columns) => columns.map((column) => <HelpLabel key={column.label} label={column.label} content={column.help} />);

const ExplanationList = ({ items = [] }) => (
  <ul style={{ margin: 0, paddingLeft: "18px" }}>
    {items.map((item, index) => (
      <li key={`${item}-${index}`}>
        <Text as="span" tone="subdued">
          {item}
        </Text>
      </li>
    ))}
  </ul>
);

const CalculationNotes = ({ title, items }) => (
  <div style={{ border: "1px solid #dfe3e8", borderRadius: "8px", padding: "12px", background: "#fafbfb" }}>
    <BlockStack gap="150">
      <Text variant="headingSm" as="h3">
        {title}
      </Text>
      <ExplanationList items={items} />
    </BlockStack>
  </div>
);

const getSelectableCellStyle = ({ selected, first, last, align }) => ({
  padding: "9px 10px",
  minHeight: "44px",
  verticalAlign: "middle",
  textAlign: align,
  cursor: "pointer",
  background: selected ? "#eef6ff" : "#fff",
  borderTop: selected ? "1px solid #5ca9ff" : "1px solid transparent",
  borderBottom: selected ? "1px solid #5ca9ff" : "1px solid #e1e3e5",
  borderLeft: selected ? (first ? "2px solid #1f6feb" : "0") : "0",
  borderRight: selected && last ? "1px solid #5ca9ff" : "0",
  borderTopLeftRadius: selected && first ? "6px" : 0,
  borderBottomLeftRadius: selected && first ? "6px" : 0,
  borderTopRightRadius: selected && last ? "6px" : 0,
  borderBottomRightRadius: selected && last ? "6px" : 0,
});

const SelectableDataTable = ({ headings, columnContentTypes, rows, selectedIndex, onSelect }) => (
  <div style={{ overflowX: "auto" }}>
    <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: 0, tableLayout: "auto" }}>
      <thead>
        <tr>
          {headings.map((heading, index) => (
            <th
              key={`selectable-heading-${index}`}
              style={{
                padding: "8px 10px",
                borderBottom: "1px solid #dfe3e8",
                fontWeight: 500,
                textAlign: columnContentTypes[index] === "numeric" ? "right" : "left",
                whiteSpace: "nowrap",
              }}>
              {heading}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, rowIndex) => {
          const selected = rowIndex === selectedIndex;
          return (
            <tr key={`selectable-row-${rowIndex}`} aria-selected={selected} onClick={() => onSelect(rowIndex)}>
              {row.map((cell, cellIndex) => (
                <td
                  key={`selectable-cell-${rowIndex}-${cellIndex}`}
                  style={getSelectableCellStyle({
                    selected,
                    first: cellIndex === 0,
                    last: cellIndex === row.length - 1,
                    align: columnContentTypes[cellIndex] === "numeric" ? "right" : "left",
                  })}>
                  {cell}
                </td>
              ))}
            </tr>
          );
        })}
      </tbody>
    </table>
  </div>
);

const moveSelection = ({ event, items, selectedKey, getKey, setSelectedKey }) => {
  if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return;
  if (!items.length) return;

  event.preventDefault();
  const currentIndex = items.findIndex((item) => String(getKey(item)) === String(selectedKey));
  const fallbackIndex = currentIndex === -1 ? 0 : currentIndex;
  const nextIndex = event.key === "ArrowDown" ? Math.min(fallbackIndex + 1, items.length - 1) : Math.max(fallbackIndex - 1, 0);
  setSelectedKey(getKey(items[nextIndex]));
};

const formatPlanningWarning = (warning) => {
  if (warning.type === "MACHINE_CONFIG_MISSING") return `Machine configuration could not be read for order ${warning.orderNumber || warning.orderId}.`;
  if (warning.type === "TREE_QUERY_SKIPPED") return `Tree query skipped for ${warning.structureName || "machine"} ${warning.variantName || ""}.`;
  if (warning.type === "TREE_BOM_EMPTY") return `No enabled tree components found for ${warning.structureName || "machine"} ${warning.variantName || ""}.`;
  if (warning.type === "TREE_BOM_ERROR") return `Tree lookup failed for ${warning.structureName || "machine"} ${warning.variantName || ""}: ${warning.message || "unknown error"}.`;
  return warning.type || "Planning warning";
};

const PlanningWarnings = ({ warnings = [] }) => {
  if (!warnings.length) return null;

  const visibleWarnings = warnings.slice(0, 5).map(formatPlanningWarning);
  const hiddenCount = warnings.length - visibleWarnings.length;

  return (
    <Banner tone="warning" title={`${warnings.length} planning warning${warnings.length === 1 ? "" : "s"}`}>
      <BlockStack gap="150">
        <Text as="p">Some machine variants or BOMs could not be resolved. They are kept visible as direct shortages where possible.</Text>
        <ExplanationList items={hiddenCount > 0 ? [...visibleWarnings, `Plus ${hiddenCount} more warning${hiddenCount === 1 ? "" : "s"}.`] : visibleWarnings} />
      </BlockStack>
    </Banner>
  );
};

const PlanAssumptions = () => (
  <Card>
    <Box padding="300">
      <CalculationNotes title="How the plan is calculated" items={PLAN_ASSUMPTIONS} />
    </Box>
  </Card>
);

const statusBadge = (status) => {
  const meta = STATUS_META[status] || { label: status || "Unknown", tone: "" };
  return (
    <ExplainedBadge content={meta.help}>
      <Badge tone={meta.tone}>{meta.label}</Badge>
    </ExplainedBadge>
  );
};

const orderStatusBadge = (status) => {
  const meta = ORDER_STATUS_META[status] || { label: status || "Unknown", tone: "" };
  return (
    <ExplainedBadge content={meta.help}>
      <Badge tone={meta.tone}>{meta.label}</Badge>
    </ExplainedBadge>
  );
};

const formatQty = (value) => {
  const number = Number(value || 0);
  return new Intl.NumberFormat("nl-BE", { maximumFractionDigits: 2 }).format(Math.round((number + Number.EPSILON) * 100) / 100);
};

const formatDate = (date) => {
  if (!date) return "-";
  return String(date).substring(0, 10);
};

const sourceBadge = (sourceType) => {
  const meta = SOURCE_META[sourceType] || { label: sourceType, tone: undefined, help: "Demand source used by the material planner." };
  return (
    <ExplainedBadge content={meta.help}>
      <Badge tone={meta.tone}>{meta.label}</Badge>
    </ExplainedBadge>
  );
};

const StatBox = ({ title, value, tone = "#5c6ac4", helpText }) => (
  <div
    style={{
      border: "1px solid #dfe3e8",
      borderRadius: "8px",
      padding: "12px 14px",
      minHeight: "78px",
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      background: "#f6f6f7",
      borderTop: `2px solid ${tone}`,
    }}>
    <BlockStack gap="100">
      <Text as="p" tone="subdued">
        <HelpLabel label={title} content={helpText} />
      </Text>
      <Text variant="headingMd" as="p">
        {value}
      </Text>
    </BlockStack>
  </div>
);

const StockLine = ({ warehouseQty, trianvalQty, incomingQty }) => (
  <BlockStack gap="050">
    <Text as="span">
      <HelpLabel label={`WH ${formatQty(warehouseQty)}`} content="Warehouse stock in AHA stock place 1." />
    </Text>
    <Text as="span" tone="subdued">
      <HelpLabel label={`TR ${formatQty(trianvalQty)}`} content="Trianval stock in AHA stock place 9. It counts as available material, but a warehouse-only shortage becomes Move stock. Negative stock is treated as 0." />
      {" | "}
      <HelpLabel label={`PO ${formatQty(incomingQty)}`} content="Open purchase quantity that has not been received yet. It reduces Short after PO but is not current stock." />
    </Text>
  </BlockStack>
);

const OrderLinks = ({ orders = [] }) => {
  if (!orders.length) return "-";
  return (
    <InlineStack gap="100">
      {orders.slice(0, 4).map((order, index) => (
        <span key={`${order.orderId || order}-${index}`}>
          {order.orderId ? <Link url={`/sales/${order.orderId}`}>{order.orderNumber}</Link> : order}
          {index < Math.min(orders.length, 4) - 1 ? ", " : ""}
        </span>
      ))}
      {orders.length > 4 && <Text as="span" tone="subdued">{`+${orders.length - 4}`}</Text>}
    </InlineStack>
  );
};

const MaterialDetail = ({ material }) => {
  if (!material) {
    return (
      <Card>
        <Box padding="400">
          <Text as="p" tone="subdued">
            Select a material to inspect the demand behind it.
          </Text>
        </Box>
      </Card>
    );
  }

  const usedByRows = material.usedBy.map((item) => [
    <BlockStack gap="050">
      <InlineStack gap="100" blockAlign="center">
        {sourceBadge(item.sourceType)}
        <Link removeUnderline url={`/products/${item.productId}`}>
          #{item.productId}
        </Link>
      </InlineStack>
      <Text as="span">{truncate(item.productName, 44)}</Text>
      {item.configString && <Text as="span" tone="subdued">{item.configString}</Text>}
    </BlockStack>,
    formatQty(item.requiredQty),
    formatDate(item.earliestDueDate),
    <OrderLinks orders={item.orders} />,
  ]);

  const demandRows = material.demandLines.slice(0, 12).map((line) => [
    <Link removeUnderline url={`/sales/${line.orderId}`}>
      {line.orderNumber}
    </Link>,
    truncate(line.customerName, 24),
    formatDate(line.parsedDeliveryTerm || line.deliveryTerm),
    truncate(line.finalProductName, 32),
    formatQty(line.qty),
  ]);

  const consumptionRows = (material.monthlyConsumption || []).map((month) => [month.monthLabel, formatQty(month.consumedQty), month.weight]);

  return (
    <Card>
      <Box padding="300">
        <InlineStack align="space-between" wrap={false} gap="300" blockAlign="center">
          <InlineStack gap="200" blockAlign="center">
            <Link removeUnderline url={`/products/${material.productId}`}>
              #{material.productId}
            </Link>
            <Text as="span">{truncate(material.productName, 52)}</Text>
          </InlineStack>
          <Link removeUnderline url={`/products/${material.productId}`}>
            Open product
          </Link>
        </InlineStack>
      </Box>
      <Box padding="300">
        <BlockStack gap="400">
          <InlineStack gap="300" wrap>
            <StatBox title="Needed" value={formatQty(material.requiredQty)} tone="#624bff" helpText={MATERIAL_METRIC_HELP.needed} />
            <StatBox title="Avg/mo" value={formatQty(material.weightedMonthlyConsumptionQty)} tone="#1f6feb" helpText={MATERIAL_METRIC_HELP.monthlyConsumption} />
            <StatBox title="Available" value={formatQty(material.availableQty)} tone="#2c6e49" helpText={MATERIAL_METRIC_HELP.available} />
            <StatBox title="Short now" value={formatQty(material.shortageQty)} tone={material.shortageQty > 0 ? "#bf0711" : "#2c6e49"} helpText={MATERIAL_METRIC_HELP.shortNow} />
            <StatBox
              title="Short after PO"
              value={formatQty(material.shortageAfterIncomingQty)}
              tone={material.shortageAfterIncomingQty > 0 ? "#bf0711" : "#d89b00"}
              helpText={MATERIAL_METRIC_HELP.shortAfterPo}
            />
          </InlineStack>
          {material.incomingOrders && (
            <Banner tone={material.shortageAfterIncomingQty > 0 ? "warning" : "info"} title={`Incoming purchase coverage: ${formatQty(material.incomingQty)}`}>
              <Text as="p">{material.incomingOrders}</Text>
            </Banner>
          )}
          <CalculationNotes
            title="Material assumptions"
            items={[
              "Needed is built only from finished-product shortages, not from every open order line if warehouse stock already covers the finished product.",
              `Avg/mo uses ${material.consumptionPeriodLabel || "the last six completed calendar months"}: sum(month consumption x weight 1..6) / 21.`,
              `Six-month used total for this part is ${formatQty(material.sixMonthConsumptionQty)}. Months without negative stock changes count as 0.`,
              "Raw material demand uses leaf components from BOM/tree explosions; parent assemblies remain visible in production detail but are not counted as raw material pressure.",
              "Available stock includes warehouse and Trianval. Purchase orders are shown separately because they are expected future coverage, not current stock.",
            ]}
          />
          <BlockStack gap="200">
            <Text variant="headingSm" as="h3">
              Consumption
            </Text>
            <DataTable columnContentTypes={["text", "numeric", "numeric"]} headings={dataHeadings(CONSUMPTION_COLUMNS)} rows={consumptionRows} increasedTableDensity />
          </BlockStack>
          <BlockStack gap="200">
            <Text variant="headingSm" as="h3">
              Used by
            </Text>
            <DataTable columnContentTypes={["text", "numeric", "text", "text"]} headings={dataHeadings(USED_BY_COLUMNS)} rows={usedByRows} increasedTableDensity />
          </BlockStack>
          <BlockStack gap="200">
            <Text variant="headingSm" as="h3">
              Earliest order pressure
            </Text>
            <DataTable columnContentTypes={["text", "text", "text", "text", "numeric"]} headings={dataHeadings(MATERIAL_ORDER_COLUMNS)} rows={demandRows} increasedTableDensity />
            {material.demandLines.length > demandRows.length && (
              <Text as="p" tone="subdued">
                Showing the earliest {demandRows.length} of {material.demandLines.length} order-pressure rows.
              </Text>
            )}
          </BlockStack>
        </BlockStack>
      </Box>
    </Card>
  );
};

const FinalProductDetail = ({ product }) => {
  if (!product) {
    return (
      <Card>
        <Box padding="400">
          <Text as="p" tone="subdued">
            Select a product to inspect its BOM or tree.
          </Text>
        </Box>
      </Card>
    );
  }

  const componentRows = product.components.map((component) => {
    const availableQty = Number(component.availableQty || 0);
    const requiredQty = Number(component.requiredQty || 0);
    const shortageQty = Math.max(requiredQty - availableQty, 0);

    return [
      <InlineStack gap="100" blockAlign="center" wrap={false}>
        <span style={{ display: "inline-block", width: `${Math.max(Number(component.level || 1) - 1, 0) * 18}px` }} />
        <Link removeUnderline url={`/products/${component.productId}`}>
          #{component.productId}
        </Link>
        <Text as="span">{truncate(component.productName, 38)}</Text>
      </InlineStack>,
      component.isLeaf ? <Badge tone="info">Raw</Badge> : <Badge>Assembly</Badge>,
      formatQty(component.qtyPerAssembly),
      formatQty(requiredQty),
      <StockLine warehouseQty={component.warehouseQty} trianvalQty={component.trianvalQty} incomingQty={component.incomingQty || 0} />,
      shortageQty > 0 ? <Badge tone="critical">{`-${formatQty(shortageQty)}`}</Badge> : <Badge tone="success">OK</Badge>,
    ];
  });

  const orderRows = product.shortageOrders.map((order) => [
    <Link removeUnderline url={`/sales/${order.orderId}`}>
      {order.orderNumber}
    </Link>,
    truncate(order.customerName, 28),
    truncate(order.customerReference, 24),
    formatDate(order.parsedDeliveryTerm || order.deliveryTerm),
    formatQty(order.qty),
  ]);

  return (
    <Card>
      <Box padding="300">
        <InlineStack align="space-between" wrap={false} gap="300" blockAlign="center">
          <InlineStack gap="200" blockAlign="center">
            {sourceBadge(product.sourceType)}
            <Link removeUnderline url={`/products/${product.productId}`}>
              #{product.productId}
            </Link>
            <Text as="span">{truncate(product.productName, 52)}</Text>
          </InlineStack>
          <Link removeUnderline url={`/products/${product.productId}`}>
            Open product
          </Link>
        </InlineStack>
      </Box>
      <Box padding="300">
        <BlockStack gap="400">
          <InlineStack gap="300" wrap>
            <StatBox title="Open demand" value={formatQty(product.requiredQty)} tone="#624bff" helpText={PRODUCTION_METRIC_HELP.openDemand} />
            <StatBox title="Warehouse stock" value={formatQty(product.warehouseQty)} tone="#2c6e49" helpText={PRODUCTION_METRIC_HELP.warehouseStock} />
            <StatBox
              title="Needs production"
              value={formatQty(product.stockShortageQty)}
              tone={product.stockShortageQty > 0 ? "#d89b00" : "#2c6e49"}
              helpText={PRODUCTION_METRIC_HELP.needsProduction}
            />
            <StatBox
              title="New work-order gap"
              value={formatQty(product.productionGapQty)}
              tone={product.productionGapQty > 0 ? "#bf0711" : "#2c6e49"}
              helpText={PRODUCTION_METRIC_HELP.newWorkOrderGap}
            />
          </InlineStack>

          {product.workOrders && (
            <Banner tone={product.productionGapQty > 0 ? "warning" : "info"} title={`Open work orders: ${formatQty(product.openWorkOrderQty)}`}>
              <Text as="p">{product.workOrders}</Text>
            </Banner>
          )}

          <CalculationNotes
            title="Production assumptions"
            items={[
              product.sourceType === "TREE"
                ? "This is a machine-tree row. The PMG/PMJ configuration chooses the Greg/Jack tree and the matching variant column."
                : "This is a BOM row. Components are expanded recursively through AHA BOM entries.",
              "Warehouse stock is allocated to the earliest due demand before production shortage is calculated.",
              "Open work orders reduce the new work-order gap, but raw material pressure still includes the parts needed to complete those work orders.",
            ]}
          />

          <BlockStack gap="200">
            <Text variant="headingSm" as="h3">
              Components
            </Text>
            {componentRows.length > 0 ? (
              <DataTable
                columnContentTypes={["text", "text", "numeric", "numeric", "text", "text"]}
                headings={dataHeadings(COMPONENT_COLUMNS)}
                rows={componentRows}
                increasedTableDensity
              />
            ) : (
              <Banner tone="warning" title="No BOM or tree components found">
                <Text as="p">The shortage is shown as a direct buy/order need for the finished part.</Text>
              </Banner>
            )}
          </BlockStack>

          <BlockStack gap="200">
            <Text variant="headingSm" as="h3">
              Orders causing the shortage
            </Text>
            <DataTable columnContentTypes={["text", "text", "text", "text", "numeric"]} headings={dataHeadings(SHORTAGE_ORDER_COLUMNS)} rows={orderRows} increasedTableDensity />
          </BlockStack>
        </BlockStack>
      </Box>
    </Card>
  );
};

const OrderDetail = ({ order }) => {
  if (!order) {
    return (
      <Card>
        <Box padding="400">
          <Text as="p" tone="subdued">
            Select an order to inspect the virtual stock reservation behind it.
          </Text>
        </Box>
      </Card>
    );
  }

  const lineRows = (order.lines || []).map((line) => [
    <BlockStack gap="050">
      <InlineStack gap="100" blockAlign="center">
        {sourceBadge(line.sourceType)}
        {line.productId ? (
          <Link removeUnderline url={`/products/${line.productId}`}>
            #{line.productId}
          </Link>
        ) : (
          <Text as="span" tone="subdued">
            No product
          </Text>
        )}
      </InlineStack>
      <Text as="span">{truncate(line.productName || "", 42)}</Text>
      {line.configString && <Text as="span" tone="subdued">{line.configString}</Text>}
    </BlockStack>,
    formatQty(line.requiredQty),
    formatQty(line.finishedStockReservedQty),
    formatQty(line.productionQty),
    line.missingQty > 0 ? <Badge tone="critical">{formatQty(line.missingQty)}</Badge> : <Badge tone="success">0</Badge>,
    orderStatusBadge(line.status),
  ]);

  const bottleneckRows = (order.bottlenecks || []).map((bottleneck) => [
    <BlockStack gap="050">
      <InlineStack gap="100" blockAlign="center">
        {sourceBadge(bottleneck.sourceType)}
        {bottleneck.productId ? (
          <Link removeUnderline url={`/products/${bottleneck.productId}`}>
            #{bottleneck.productId}
          </Link>
        ) : (
          <Text as="span" tone="subdued">
            No product
          </Text>
        )}
      </InlineStack>
      <Text as="span">{truncate(bottleneck.productName || "", 42)}</Text>
    </BlockStack>,
    formatQty(bottleneck.requiredQty),
    formatQty(bottleneck.reservedQty),
    <Badge tone="critical">{formatQty(bottleneck.missingQty)}</Badge>,
    formatQty(bottleneck.firstRemainingBeforeQty),
    truncate((bottleneck.usedBy || []).join(", "), 40),
    truncate(bottleneck.issue || "Not enough current stock", 36),
  ]);

  const reservationNotes = [
    "This is a virtual reservation queue only. It does not write reservations, stock moves, or work orders to the database.",
    "Orders are sorted by parsed delivery date first, then order id. That is why order 3 only sees stock that remains after orders 1 and 2 have reserved.",
    "Normal BOM products first consume finished warehouse stock. The remaining quantity is exploded into leaf components.",
    "Greg/Jack machine lines are build-to-order: they do not consume finished-machine stock, and their PMG/PMJ tree parts are reserved directly.",
    "Raw material availability is current warehouse plus Trianval stock. Incoming purchase orders and open work orders are intentionally not counted in this order view.",
  ];

  return (
    <Card>
      <Box padding="300">
        <InlineStack align="space-between" wrap={false} gap="300" blockAlign="center">
          <InlineStack gap="200" blockAlign="center">
            <Badge tone="info">{`#${order.sequenceNumber}`}</Badge>
            <Link removeUnderline url={`/sales/${order.orderId}`}>
              {order.orderNumber}
            </Link>
            {orderStatusBadge(order.status)}
          </InlineStack>
          <Link removeUnderline url={`/sales/${order.orderId}`}>
            Open order
          </Link>
        </InlineStack>
      </Box>
      <Box padding="300">
        <BlockStack gap="400">
          <BlockStack gap="050">
            <Text variant="headingSm" as="h3">
              {truncate(order.customerName || "Unknown customer", 64)}
            </Text>
            <Text as="p" tone="subdued">
              {order.customerReference ? `${order.customerReference} | ` : ""}
              Due {formatDate(order.parsedDeliveryTerm || order.deliveryTerm)}
            </Text>
          </BlockStack>

          <InlineStack gap="300" wrap>
            <StatBox title="Priority" value={`#${order.sequenceNumber}`} tone="#1f6feb" helpText={ORDER_METRIC_HELP.priority} />
            <StatBox title="Finished reserved" value={formatQty(order.finishedStockReservedQty)} tone="#2c6e49" helpText={ORDER_METRIC_HELP.finishedReserved} />
            <StatBox title="To build" value={formatQty(order.productionQty)} tone="#624bff" helpText={ORDER_METRIC_HELP.productionQty} />
            <StatBox
              title="Material reserved"
              value={`${formatQty(order.materialReservedQty)} / ${formatQty(order.materialRequiredQty)}`}
              tone="#0f9d58"
              helpText={ORDER_METRIC_HELP.materialReserved}
            />
            <StatBox
              title="Missing parts"
              value={formatQty(order.missingMaterialQty)}
              tone={order.missingMaterialQty > 0 ? "#bf0711" : "#2c6e49"}
              helpText={ORDER_METRIC_HELP.missingMaterial}
            />
          </InlineStack>

          {order.status === "producible" ? (
            <Banner tone="success" title="Producible with current stock">
              <Text as="p">After earlier orders reserve their stock, this order can still reserve everything it needs.</Text>
            </Banner>
          ) : (
            <Banner tone="warning" title="Stock problem forms at this order">
              <Text as="p">The bottlenecks below show the first parts that are no longer available in the virtual queue for this order.</Text>
            </Banner>
          )}

          <CalculationNotes title="Order reservation assumptions" items={reservationNotes} />

          <BlockStack gap="200">
            <Text variant="headingSm" as="h3">
              Order lines
            </Text>
            <DataTable columnContentTypes={["text", "numeric", "numeric", "numeric", "numeric", "text"]} headings={dataHeadings(ORDER_LINE_COLUMNS)} rows={lineRows} increasedTableDensity />
          </BlockStack>

          <BlockStack gap="200">
            <Text variant="headingSm" as="h3">
              Bottlenecks
            </Text>
            {bottleneckRows.length > 0 ? (
              <DataTable
                columnContentTypes={["text", "numeric", "numeric", "numeric", "numeric", "text", "text"]}
                headings={dataHeadings(ORDER_BOTTLENECK_COLUMNS)}
                rows={bottleneckRows}
                increasedTableDensity
              />
            ) : (
              <Banner tone="success" title="No bottlenecks for this order">
                <Text as="p">All required current stock can be reserved at this order's position in the queue.</Text>
              </Banner>
            )}
          </BlockStack>
        </BlockStack>
      </Box>
    </Card>
  );
};

export default function FulfilmentProduct() {
  const [plan, setPlan] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [view, setView] = useState("materials");
  const [materialStatusFilter, setMaterialStatusFilter] = useState("problem");
  const [productionStatusFilter, setProductionStatusFilter] = useState("gap");
  const [orderStatusFilter, setOrderStatusFilter] = useState("problem");
  const [search, setSearch] = useState("");
  const [selectedMaterialId, setSelectedMaterialId] = useState(null);
  const [selectedProductKey, setSelectedProductKey] = useState(null);
  const [selectedOrderId, setSelectedOrderId] = useState(null);

  useEffect(() => {
    async function fetchPlan() {
      const data = await getRequest("/api/fulfilment/material-plan");
      setPlan(data);
      setLoaded(true);
      setSelectedMaterialId(data?.materials?.[0]?.productId || null);
      setSelectedProductKey(data?.finalProducts?.find((product) => product.productionGapQty > 0)?.key || data?.finalProducts?.[0]?.key || null);
      setSelectedOrderId(data?.orderFulfilment?.find((order) => order.status !== "producible")?.orderId || data?.orderFulfilment?.[0]?.orderId || null);
    }
    fetchPlan();
  }, []);

  const materials = plan?.materials || [];
  const finalProducts = plan?.finalProducts || [];
  const orders = plan?.orderFulfilment || [];

  const filteredMaterials = useMemo(() => {
    const normalizedSearch = search.toLowerCase();
    return materials
      .filter((material) => {
        if (materialStatusFilter === "problem") return material.status === "critical" || material.status === "incoming" || material.status === "move";
        if (materialStatusFilter !== "all") return material.status === materialStatusFilter;
        return true;
      })
      .filter((material) => {
        if (!normalizedSearch) return true;
        return `${material.productId} ${material.productName} ${material.supplierName || ""}`.toLowerCase().includes(normalizedSearch);
      });
  }, [materials, materialStatusFilter, search]);

  const filteredFinalProducts = useMemo(() => {
    const normalizedSearch = search.toLowerCase();
    return finalProducts
      .filter((product) => {
        if (productionStatusFilter === "gap") return product.productionGapQty > 0 || product.status === "needs_order";
        if (productionStatusFilter !== "all") return product.status === productionStatusFilter;
        return true;
      })
      .filter((product) => {
        if (!normalizedSearch) return true;
        return `${product.productId} ${product.productName} ${product.configString || ""}`.toLowerCase().includes(normalizedSearch);
      });
  }, [finalProducts, productionStatusFilter, search]);

  const filteredOrders = useMemo(() => {
    const normalizedSearch = search.toLowerCase();
    return orders
      .filter((order) => {
        if (orderStatusFilter === "problem") return order.status !== "producible";
        if (orderStatusFilter !== "all") return order.status === orderStatusFilter;
        return true;
      })
      .filter((order) => {
        if (!normalizedSearch) return true;
        const lineText = (order.lines || []).map((line) => `${line.productId} ${line.productName || ""} ${line.configString || ""}`).join(" ");
        return `${order.orderNumber} ${order.orderId} ${order.customerName || ""} ${order.customerReference || ""} ${lineText}`.toLowerCase().includes(normalizedSearch);
      });
  }, [orders, orderStatusFilter, search]);

  useEffect(() => {
    if (!filteredMaterials.length) return;
    const selectedVisible = filteredMaterials.some((material) => Number(material.productId) === Number(selectedMaterialId));
    if (!selectedVisible) setSelectedMaterialId(filteredMaterials[0].productId);
  }, [filteredMaterials, selectedMaterialId]);

  useEffect(() => {
    if (!filteredFinalProducts.length) return;
    const selectedVisible = filteredFinalProducts.some((product) => product.key === selectedProductKey);
    if (!selectedVisible) setSelectedProductKey(filteredFinalProducts[0].key);
  }, [filteredFinalProducts, selectedProductKey]);

  useEffect(() => {
    if (!filteredOrders.length) return;
    const selectedVisible = filteredOrders.some((order) => Number(order.orderId) === Number(selectedOrderId));
    if (!selectedVisible) setSelectedOrderId(filteredOrders[0].orderId);
  }, [filteredOrders, selectedOrderId]);

  if (!loaded) return <PageSpinner />;

  const selectedMaterial = filteredMaterials.find((material) => Number(material.productId) === Number(selectedMaterialId)) || filteredMaterials[0] || materials[0];
  const selectedProduct = filteredFinalProducts.find((product) => product.key === selectedProductKey) || filteredFinalProducts[0] || finalProducts[0];
  const selectedOrder = filteredOrders.find((order) => Number(order.orderId) === Number(selectedOrderId)) || filteredOrders[0] || orders[0];

  const handleMaterialKeyDown = (event) =>
    moveSelection({
      event,
      items: filteredMaterials,
      selectedKey: selectedMaterialId,
      getKey: (material) => material.productId,
      setSelectedKey: setSelectedMaterialId,
    });

  const handleProductionKeyDown = (event) =>
    moveSelection({
      event,
      items: filteredFinalProducts,
      selectedKey: selectedProductKey,
      getKey: (product) => product.key,
      setSelectedKey: setSelectedProductKey,
    });

  const handleOrderKeyDown = (event) =>
    moveSelection({
      event,
      items: filteredOrders,
      selectedKey: selectedOrderId,
      getKey: (order) => order.orderId,
      setSelectedKey: setSelectedOrderId,
    });

  const selectedMaterialIndex = filteredMaterials.findIndex((material) => Number(material.productId) === Number(selectedMaterial?.productId));
  const selectedProductIndex = filteredFinalProducts.findIndex((product) => product.key === selectedProduct?.key);
  const selectedOrderIndex = filteredOrders.findIndex((order) => Number(order.orderId) === Number(selectedOrder?.orderId));

  const materialRows = filteredMaterials.map((material) => {
    const isSelected = Number(material.productId) === Number(selectedMaterial?.productId);
    const selectMaterial = () => setSelectedMaterialId(material.productId);

    return [
      <BlockStack gap="050">
        <InlineStack gap="100" blockAlign="center">
          <Link removeUnderline url={`/products/${material.productId}`}>
            #{material.productId}
          </Link>
          <Text as="span" fontWeight={isSelected ? "semibold" : "regular"}>
            {truncate(material.productName, 42)}
          </Text>
        </InlineStack>
        <Text as="span" tone="subdued">
          {truncate(material.supplierName, 42)}
        </Text>
      </BlockStack>,
      formatQty(material.requiredQty),
      formatQty(material.weightedMonthlyConsumptionQty),
      <StockLine warehouseQty={material.warehouseQty} trianvalQty={material.trianvalQty} incomingQty={material.incomingQty} />,
      material.shortageAfterIncomingQty > 0 ? <Badge tone="critical">{formatQty(material.shortageAfterIncomingQty)}</Badge> : formatQty(material.shortageQty),
      formatDate(material.earliestDueDate),
      statusBadge(material.status),
      <div onClick={(event) => event.stopPropagation()}>
        <Button size="slim" pressed={isSelected} onClick={selectMaterial}>
          Inspect
        </Button>
      </div>,
    ];
  });

  const productionRows = filteredFinalProducts.map((product) => {
    const isSelected = product.key === selectedProduct?.key;
    const selectProduct = () => setSelectedProductKey(product.key);

    return [
      <BlockStack gap="050">
        <InlineStack gap="100" blockAlign="center">
          {sourceBadge(product.sourceType)}
          <Link removeUnderline url={`/products/${product.productId}`}>
            #{product.productId}
          </Link>
        </InlineStack>
        <Text as="span" fontWeight={isSelected ? "semibold" : "regular"}>
          {truncate(product.productName, 42)}
        </Text>
      </BlockStack>,
      formatQty(product.requiredQty),
      formatQty(product.warehouseQty),
      formatQty(product.stockShortageQty),
      formatQty(product.openWorkOrderQty),
      product.productionGapQty > 0 ? <Badge tone="critical">{formatQty(product.productionGapQty)}</Badge> : <Badge tone="success">0</Badge>,
      formatDate(product.earliestDueDate),
      statusBadge(product.status),
      <div onClick={(event) => event.stopPropagation()}>
        <Button size="slim" pressed={isSelected} onClick={selectProduct}>
          Inspect
        </Button>
      </div>,
    ];
  });

  const orderRows = filteredOrders.map((order) => {
    const isSelected = Number(order.orderId) === Number(selectedOrder?.orderId);
    const selectOrder = () => setSelectedOrderId(order.orderId);

    return [
      <BlockStack gap="050">
        <InlineStack gap="100" blockAlign="center">
          <Badge tone="info">{`#${order.sequenceNumber}`}</Badge>
          <Link removeUnderline url={`/sales/${order.orderId}`}>
            {order.orderNumber}
          </Link>
        </InlineStack>
        <Text as="span" tone="subdued">
          {`Virtual reservation position ${order.sequenceNumber}`}
        </Text>
      </BlockStack>,
      <BlockStack gap="050">
        <Text as="span" fontWeight={isSelected ? "semibold" : "regular"}>
          {truncate(order.customerName || "", 30)}
        </Text>
        <Text as="span" tone="subdued">
          {truncate(order.customerReference || "", 30)}
        </Text>
      </BlockStack>,
      formatDate(order.parsedDeliveryTerm || order.deliveryTerm),
      formatQty(order.lineCount),
      formatQty(order.productionQty),
      order.missingMaterialQty > 0 ? <Badge tone="critical">{formatQty(order.missingMaterialQty)}</Badge> : <Badge tone="success">0</Badge>,
      order.bottleneckCount > 0 ? <Badge tone="critical">{order.bottleneckCount}</Badge> : <Badge tone="success">0</Badge>,
      orderStatusBadge(order.status),
      <div onClick={(event) => event.stopPropagation()}>
        <Button size="slim" pressed={isSelected} onClick={selectOrder}>
          Inspect
        </Button>
      </div>,
    ];
  });

  return (
    <BlockStack gap="400">
      <PlanningWarnings warnings={plan?.warnings || []} />

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: "12px" }}>
        <StatBox title="Open order lines" value={plan?.summary?.openOrderLines || 0} tone="#1f6feb" helpText={SUMMARY_HELP.openOrderLines} />
        <StatBox title="Machine slots" value={plan?.summary?.machineProductionSlots || 0} tone="#0f9d58" helpText={SUMMARY_HELP.machineProductionSlots} />
        <StatBox title="Production gaps" value={plan?.summary?.finalProductsWithProductionGap || 0} tone="#d89b00" helpText={SUMMARY_HELP.finalProductsWithProductionGap} />
        <StatBox title="Raw materials" value={plan?.summary?.materials || 0} tone="#624bff" helpText={SUMMARY_HELP.materials} />
        <StatBox title="Current shortages" value={plan?.summary?.materialsWithCurrentShortage || 0} tone="#bf0711" helpText={SUMMARY_HELP.materialsWithCurrentShortage} />
        <StatBox title="Critical after PO" value={plan?.summary?.criticalMaterials || 0} tone="#8a6116" helpText={SUMMARY_HELP.criticalMaterials} />
      </div>

      <PlanAssumptions />

      <Card>
        <Box padding="300">
          <InlineStack align="space-between" gap="300" blockAlign="center">
            <ButtonGroup variant="segmented">
              <Button pressed={view === "materials"} onClick={() => setView("materials")}>
                Raw material explorer
              </Button>
              <Button pressed={view === "production"} onClick={() => setView("production")}>
                Production explorer
              </Button>
              <Button pressed={view === "orders"} onClick={() => setView("orders")}>
                Order explorer
              </Button>
            </ButtonGroup>
            <div style={{ minWidth: "280px" }}>
              <TextField
                label="Search"
                labelHidden
                value={search}
                onChange={setSearch}
                autoComplete="off"
                placeholder="Search part, product, supplier, order"
                helpText="Searches product IDs, names, suppliers, orders, customers, references, and machine configuration strings where available."
              />
            </div>
          </InlineStack>
        </Box>
      </Card>

      {view === "materials" ? (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(360px, 1fr))", gap: "16px", alignItems: "start" }}>
          <Card>
            <CardHeading title="Raw material pressure" />
            <Box padding="300">
              <BlockStack gap="300">
                <ButtonGroup variant="segmented">
                  {[
                    ["problem", "Problems"],
                    ["critical", "Critical"],
                    ["incoming", "Incoming"],
                    ["move", "Move"],
                    ["all", "All"],
                  ].map(([value, label]) => (
                    <Button key={value} size="slim" pressed={materialStatusFilter === value} onClick={() => setMaterialStatusFilter(value)}>
                      {label}
                    </Button>
                  ))}
                </ButtonGroup>
                <div tabIndex={0} onKeyDown={handleMaterialKeyDown} onClick={(event) => event.currentTarget.focus()} aria-label="Raw material rows" style={{ outline: "none" }}>
                  <SelectableDataTable
                    columnContentTypes={["text", "numeric", "numeric", "text", "numeric", "text", "text", "text"]}
                    headings={dataHeadings(MATERIAL_COLUMNS)}
                    rows={materialRows}
                    selectedIndex={selectedMaterialIndex}
                    onSelect={(rowIndex) => setSelectedMaterialId(filteredMaterials[rowIndex]?.productId)}
                  />
                </div>
              </BlockStack>
            </Box>
          </Card>
          <MaterialDetail material={selectedMaterial} />
        </div>
      ) : view === "production" ? (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(380px, 1fr))", gap: "16px", alignItems: "start" }}>
          <Card>
            <CardHeading title="What needs production or ordering" />
            <Box padding="300">
              <BlockStack gap="300">
                <ButtonGroup variant="segmented">
                  {[
                    ["gap", "Gaps"],
                    ["needs_production", "Production"],
                    ["needs_order", "No BOM"],
                    ["planned", "Planned"],
                    ["all", "All"],
                  ].map(([value, label]) => (
                    <Button key={value} size="slim" pressed={productionStatusFilter === value} onClick={() => setProductionStatusFilter(value)}>
                      {label}
                    </Button>
                  ))}
                </ButtonGroup>
                <div tabIndex={0} onKeyDown={handleProductionKeyDown} onClick={(event) => event.currentTarget.focus()} aria-label="Production rows" style={{ outline: "none" }}>
                  <SelectableDataTable
                    columnContentTypes={["text", "numeric", "numeric", "numeric", "numeric", "numeric", "text", "text", "text"]}
                    headings={dataHeadings(PRODUCTION_COLUMNS)}
                    rows={productionRows}
                    selectedIndex={selectedProductIndex}
                    onSelect={(rowIndex) => setSelectedProductKey(filteredFinalProducts[rowIndex]?.key)}
                  />
                </div>
              </BlockStack>
            </Box>
          </Card>
          <FinalProductDetail product={selectedProduct} />
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(390px, 1fr))", gap: "16px", alignItems: "start" }}>
          <Card>
            <CardHeading title="Order producibility queue" />
            <Box padding="300">
              <BlockStack gap="300">
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))", gap: "10px" }}>
                  <StatBox title="Open orders" value={plan?.summary?.orderFulfilmentOrders || plan?.summary?.openOrders || 0} tone="#1f6feb" helpText="Open orders represented in this virtual reservation queue." />
                  <StatBox
                    title="Producible"
                    value={plan?.summary?.producibleOrders || 0}
                    tone="#2c6e49"
                    helpText="Orders that can be fulfilled from current finished stock or current material stock after earlier orders reserve first."
                  />
                  <StatBox
                    title="Problems"
                    value={plan?.summary?.problemOrders || 0}
                    tone="#bf0711"
                    helpText="Orders that become partial or blocked in the virtual queue because current stock is missing or a BOM/tree cannot be resolved."
                  />
                </div>
                <CalculationNotes
                  title="How order reservation works"
                  items={[
                    "This view walks the orders from earliest due date to latest due date and reserves current stock as it goes.",
                    "Order 3 therefore sees only the stock left after orders 1 and 2 have virtually reserved their finished products and raw materials.",
                    "No database reservation is written. This is a planning simulation to show where fulfilment starts to break.",
                  ]}
                />
                <ButtonGroup variant="segmented">
                  {[
                    ["problem", "Problems"],
                    ["blocked", "Blocked"],
                    ["partial", "Partial"],
                    ["producible", "Producible"],
                    ["all", "All"],
                  ].map(([value, label]) => (
                    <Button key={value} size="slim" pressed={orderStatusFilter === value} onClick={() => setOrderStatusFilter(value)}>
                      {label}
                    </Button>
                  ))}
                </ButtonGroup>
                <div tabIndex={0} onKeyDown={handleOrderKeyDown} onClick={(event) => event.currentTarget.focus()} aria-label="Order rows" style={{ outline: "none" }}>
                  <SelectableDataTable
                    columnContentTypes={["text", "text", "text", "numeric", "numeric", "numeric", "numeric", "text", "text"]}
                    headings={dataHeadings(ORDER_COLUMNS)}
                    rows={orderRows}
                    selectedIndex={selectedOrderIndex}
                    onSelect={(rowIndex) => setSelectedOrderId(filteredOrders[rowIndex]?.orderId)}
                  />
                </div>
              </BlockStack>
            </Box>
          </Card>
          <OrderDetail order={selectedOrder} />
        </div>
      )}
    </BlockStack>
  );
}
